import { Children, ReactNode, RefObject, useEffect, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { capitalize } from 'lodash';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { truncateText } from 'common/util/string';
import { setMark } from 'common/PerformanceLogger';
import ScalyrProfiler from 'components/common/ScalyrProfiler/ScalyrProfiler';

// styles
import './Page.scss';

type PageProps = {
  children?: ReactNode;
  docTitle: string;
  type?: string;
  pageModifier?: string;
  containerModifier?: string;
  hasBackground?: boolean;
  testId?: ELEMENT;
  pageRef?: RefObject<HTMLDivElement>;
};

const MAX_TITLE_LENGTH = 30;

/**
 * Page is a component for standardizing Scalyr pages. Some general styles are applied here so that minimum and maximum
 * widths are enforced, and so that page content will scroll correctly without considering surrounding app content like
 * the Nav Bar. Pages are one of two types:
 *
 *     'standard':  (default) page content is centered and does not span the entire width of a typical view port
 *     'full':      page content is left-justified and always spans at least the entire width of the view port
 *
 * @prop children           The page's content
 * @prop docTitle           Title for the browser tab
 * @prop type               standard* | full
 * @prop pageModifier       CSS classes to override defaults
 * @prop containerModifier  CSS classes to override defaults
 * @prop testId             Test id for testing
 * @prop pageRef            Ref to attach to page container (eg to perform scrolling)
 *
 * @returns {Component}
 */
function Page({
  children,
  docTitle,
  type = 'standard',
  pageModifier,
  containerModifier,
  hasBackground,
  testId,
  pageRef,
}: PageProps): JSX.Element {
  const title = docTitle ? truncateText(docTitle + ' | ' + K.PRODUCT_NAME, MAX_TITLE_LENGTH) : K.PRODUCT_NAME;

  useEffect(() => {
    document.title = title;
  }, [title]);

  useLayoutEffect(() => {
    const childCount = Children.count(children);
    setMark('pageRender', { docTitle, pageModifier, childCount });
  });

  if (!containerModifier) {
    if (type === 'standard') {
      containerModifier = 'PageContainer--' + capitalize(type);
    }
  }

  if (!pageModifier) {
    pageModifier = 'Page--' + capitalize(type);
  }

  return (
    <ScalyrProfiler id={title}>
      <div
        ref={pageRef}
        className={classNames('PageContainer', containerModifier, { 'PageContainer--HasBackground': hasBackground })}
      >
        <div className={classNames('Page', pageModifier)} data-test-id={testId}>
          {children}
        </div>
      </div>
    </ScalyrProfiler>
  );
}

export const showWaitCursor = (show = false): void => {
  document.body.className = show ? 'wait' : '';
};

export default Page;
